import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { api } from "utils/api";

function AddCustomerContactLinkDialog({ open, onClose, onSave, contactId, existingAccounts }) {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [notificationPrefs, setNotificationPrefs] = useState({
    primary_contact: "no",
    get_email: "no",
    get_whatsapp: "no",
    get_billing: "no",
    get_notification: "no",
  });
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    if (!open) return;
    setSelectedCustomer(null);
    setNotificationPrefs({
      primary_contact: "no",
      get_email: "no",
      get_whatsapp: "no",
      get_billing: "no",
      get_notification: "no",
    });
    setError(null);
  }, [open, contactId]);

  useEffect(() => {
    if (!open) return undefined;

    const fetchCustomers = async () => {
      try {
        const response = await api.post("/db/customers/filter", {
          filters: {},
          columns: ["customer_id", "code", "name"],
        });
        setCustomers(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("Error fetching customers:", err);
        setCustomers([]);
      }
    };

    void fetchCustomers();
    return undefined;
  }, [open]);

  const availableCustomers = customers.filter(
    (customer) => !existingAccounts.some((account) => account.customer_id === customer.customer_id)
  );

  const validateNotificationRequirements = async (newPrefs) => {
    if (!selectedCustomer?.customer_id) return null;

    try {
      setValidating(true);
      const response = await api.post("/db/customer_contact_links/filter", {
        filters: {
          customer_id: selectedCustomer.customer_id,
        },
      });

      const otherLinks = (Array.isArray(response) ? response : []).filter(
        (link) => link.customer_contact_id !== contactId
      );

      const hasOtherPrimary = otherLinks.some((link) => link.primary_contact === "yes");
      if (newPrefs.primary_contact === "no" && !hasOtherPrimary) {
        return "This account must have exactly one primary contact";
      }

      const notificationFields = ["get_email", "get_whatsapp", "get_billing", "get_notification"];
      for (const field of notificationFields) {
        const hasOtherNotification = otherLinks.some((link) => link[field] === "yes");
        if (newPrefs[field] === "no" && !hasOtherNotification) {
          return `This account must have at least one contact receiving ${field.replace("get_", "")}`;
        }
      }

      return null;
    } catch (err) {
      console.error("Error validating requirements:", err);
      return "Error validating requirements";
    } finally {
      setValidating(false);
    }
  };

  const handleNotificationChange = (field) => async (event) => {
    const newValue = event.target.checked ? "yes" : "no";
    const newPrefs = { ...notificationPrefs, [field]: newValue };

    if (newValue === "no") {
      const validationError = await validateNotificationRequirements(newPrefs);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setNotificationPrefs(newPrefs);
    setError(null);
  };

  const handlePrimaryChange = async (event) => {
    const newValue = event.target.checked ? "yes" : "no";
    const newPrefs = { ...notificationPrefs, primary_contact: newValue };

    if (newValue === "no") {
      const validationError = await validateNotificationRequirements(newPrefs);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setNotificationPrefs(newPrefs);
    setError(null);
  };

  const handleSave = async () => {
    try {
      if (!selectedCustomer) {
        setError("Please select an account");
        return;
      }

      const validationError = await validateNotificationRequirements(notificationPrefs);
      if (validationError) {
        setError(validationError);
        return;
      }

      await api.post("/db/customer_contact_links", {
        customer_id: selectedCustomer.customer_id,
        customer_contact_id: contactId,
        ...notificationPrefs,
      });

      onSave();
      onClose();
    } catch (err) {
      setError("Failed to create account link");
      console.error("Error creating account link:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link New Account</DialogTitle>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} mt={2}>
          <Autocomplete
            options={availableCustomers}
            getOptionLabel={(option) => `${option.code} - ${option.name}`}
            value={selectedCustomer}
            onChange={(event, newValue) => {
              setSelectedCustomer(newValue);
              setError(null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Account"
                placeholder="Search by account code or name"
                required
              />
            )}
            isOptionEqualToValue={(option, value) => option.customer_id === value?.customer_id}
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter((option) => {
                const code = option?.code?.toLowerCase() || "";
                const name = option?.name?.toLowerCase() || "";
                return code.includes(searchTerm) || name.includes(searchTerm);
              });
            }}
            noOptionsText="No accounts found"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={notificationPrefs.primary_contact === "yes"}
                onChange={handlePrimaryChange}
                disabled={validating || !selectedCustomer}
              />
            }
            label="Primary Contact"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={notificationPrefs.get_email === "yes"}
                onChange={handleNotificationChange("get_email")}
                disabled={validating || !selectedCustomer}
              />
            }
            label="Receive Email"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={notificationPrefs.get_whatsapp === "yes"}
                onChange={handleNotificationChange("get_whatsapp")}
                disabled={validating || !selectedCustomer}
              />
            }
            label="Receive WhatsApp"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={notificationPrefs.get_billing === "yes"}
                onChange={handleNotificationChange("get_billing")}
                disabled={validating || !selectedCustomer}
              />
            }
            label="Receive Billing"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={notificationPrefs.get_notification === "yes"}
                onChange={handleNotificationChange("get_notification")}
                disabled={validating || !selectedCustomer}
              />
            }
            label="Receive Notifications"
          />

          {error && (
            <MDTypography color="error" variant="caption">
              {error}
            </MDTypography>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} disabled={validating}>
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSave}
          variant="gradient"
          color="info"
          disabled={validating || !selectedCustomer}
        >
          Save
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

AddCustomerContactLinkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  contactId: PropTypes.number.isRequired,
  existingAccounts: PropTypes.arrayOf(
    PropTypes.shape({
      customer_id: PropTypes.number,
    })
  ),
};

AddCustomerContactLinkDialog.defaultProps = {
  existingAccounts: [],
};

export default AddCustomerContactLinkDialog;
